/** - crea un ConfigJSON
 * @class ConfigJSON
 * @typedef ConfigJSON
 * @property {DefaultView} DefaultView - los datos de la vista por defecto, tine la posicion de la camara, los datos del zoom y de la proyección
 * @property {[ProjectionEPSG]} AvailableEPSG - la lista de proyecciones disponibles en el visualizador, son objetos ProjectionEPSG
 * @property {[JSONLayerData]} DefaultData - tiene los datos de las capas, son objetos de tipo JSONLayerData
 */
export class ConfigJSON{

    /**
     * @constructor 
     * @param {DefaultView} DefaultView - los datos de la vista por defecto, tine la posicion de la camara, los datos del zoom y de la proyección
     * @param {[ProjectionEPSG]} AvailableEPSG - la lista de proyecciones disponibles en el visualizador, son objetos ProjectionEPSG
     * @param {[JSONLayerData]} DefaultData - tiene los datos de las capas, son objetos de tipo JSONLayerData
     */
    constructor(DefaultView, AvailableEPSG, DefaultData){
        this.DefaultView = DefaultView;
        this.AvailableEPSG = AvailableEPSG;
        this.DefaultData = DefaultData;
    }
}

/** - tiene los datos de la vista por defecto del visualizador
 * @class DefaultView
 */
export class DefaultView{
    
    /** - crea un DefaultView
     * @constructor 
     * @param {string} Projection - tiene el codigo EPSG de la proyeccion por defecto como texto, por ejemplo "EPSG:3857"
     * @param {number} MinZoom - el valor de zoom mas pequeño que se permite (el zoom mas lejano)
     * @param {number} MaxZoom - el valor de zoom mas grande que se permite (el zoom mas cercano)
     * @param {number} Zoom - el valor de zoom por defecto
     * @param {number} CenterX - las coordenadas en x de la vista por defecto
     * @param {number} CenterY - las coordenadas en y de la vista por defecto
     * @param {number} Scale - las Escala por defecto
     */
    constructor(Projection, MinZoom, MaxZoom, Zoom, CenterX, CenterY, Scale){
        this.Projection = Projection;
        this.MinZoom = MinZoom;
        this.MaxZoom = MaxZoom;
        this.Zoom = Zoom;
        this.CenterX = CenterX;
        this.CenterY = CenterY;
        this.Scale = Scale;
    }
}

/** - tiene los datos de la projeccion a mostrar (Nombre y EPSG)
 * @class ProjectionEPSG
 * @typedef ProjectionEPSG
 * @property {string} Name - el nombre personalizado de la proyeccion con el que se va a mostrar, ejemplo "WGS 84 - Web Mercator"
 * @property {number} EPSG - codigo del epsg estandard como numero, por ejemplo 4326
 */
 export class ProjectionEPSG{
    
    /** - crea un ProjectionEPSG
     * @constructor 
     * @param {string} Name - el nombre personalizado de la proyeccion con el que se va a mostrar, ejemplo "WGS 84 - Web Mercator"
     * @param {number} EPSG - codigo del epsg estandard como numero, por ejemplo 4326
     */
    constructor(Name, EPSG){
        this.Name = Name;
        this.EPSG = EPSG;
    }
}

/** - Datos de la una capa, nombre de clase JSONLayerData 
 * @class JSONLayerData
 */
 export class JSONLayerData{
    
    /** - crea un JSONLayerData
     * @constructor 
     * @param {string} Type - tipo de capa, solo para debug
     * @param {boolean} Activated - si la capa comienza activada por defecto
     * @param {string} CustomName - el nombre de la capa, este es el nombre que muestra el visualizador en el LayerDisplay
     * @param {string} Format - formato de la capa, por ejempolo "GeoJSON", "WMS".
     * @param {LayerParameteres} Parameters - tiene los parametros personalizados de capa, sus valores dependen del valor de Format, por ejemplo "GeoJSON" tiene Parameters.Symbol pero "WMS" no
     */
    constructor(Type, Activated, CustomName, Format, Parameters){
        this.Type = Type;
        this.Activated = Activated;
        this.CustomName = CustomName;
        this.Format = Format;
        this.Parameters = Parameters;
    }
}

/** - Parametros de la una capa, se usa para los parametros especiales (Ej: Simbolo, LayerNames, LayerTitles, Atributions) , nombre de clase LayerParameteres 
 * @class LayerParameteres
 */
 export class LayerParameteres{
    
    /** - crea un LayerParameteres
     * @constructor 
     * @param {string} URL - Link de donde se obtiene la capa
     * @param {[string]} LayerNames - la lista de nombres de las capas a cargar, se usan en la consulta de WMS, deben tener el mismo indice que su contraparte de LayerTitles
     * @param {[string]} LayerTitles - la lista de Titulos de las capas, se usa como nombres de capas en las consultas de informacion, deben tener el mismo indice que su contraparte de LayerNames
     * @param {[Symbol_Fill | Symbol_Stroke]} Symbol - (SOLO GeoJSON) - Tiene los datos del simbolo de la capa, actualmente compatible con Symbol_Fill, Symbol_Stroke
     * @param {string} Attributions - texto en formato HTML que se usara para rellenar el bloque de atributions que se ve en la capa en el LayerDisplay
     * @param {string} ImagFormat - (SOLO WMS) el formato de la imagen en la consulta del WMS, ej 'image/png' [IMPORTANTE: el WMS debe tener dicho formato activo]
     * @param {boolean} Transparent - (SOLO WMS) si la capa WMS se va a cargar con transparencia o no, si no se carga con transparencia el fondo sera blanco o en su defecto el que el wms tenga seteado
     * @param {boolean} Tiled - (SOLO WMS) si la capa WMS se va a cargar con transparencia o no, si no se carga con transparencia el fondo sera blanco o en su defecto el que el wms tenga seteado
     * @param {ExtentConfig} Extent - (Opcional) Tendra los parametros de la extencion y se priorizaran estos a los que responda el wms en caso de existir este parametro
     */
    constructor(URL, LayerNames, LayerTitles, Symbol, Attributions, ImagFormat, Transparent, Tiled, Extent){
        this.URL = URL;
        this.LayerNames = LayerNames;
        this.LayerTitles = LayerTitles;
        this.Symbol = Symbol;
        this.Attributions = Attributions;
        this.ImagFormat = ImagFormat;
        this.Transparent = Transparent;
        this.Tiled = Tiled;
        this.Extent = Extent;
    }
}

/** - Parametros del simbolo de tipo Relleno, Simbologia, nombre de clase Symbol_Fill 
 * @class Symbol_Fill
 */
 export class Symbol_Fill{
    
    /** - crea un Symbol_Fill
     * @constructor
     * @param {string} Type - tipo de simbolo, se usa para controles
     * @param {string} Color - color del simbolo, si este se deja en undefined o vacio sera 'rgb(0,0,0)'
     * @param {string} ZIndex - el orden del dibujado, se usa cuando se tienen muchos simbolos que se tienen que superponer en cierto ordem, por defecto el zIndex sera el orden de llegada si no se espesifica o es undefined
     */
    constructor(Type, Color, width, ZIndex){
        this.Type = Type;
        this.Color = Color;
        this.ZIndex = ZIndex;
    }
}

/** - Parametros del simbolo de tipo Linea, Simbologia, nombre de clase Symbol_Stroke 
 * @class Symbol_Stroke
 */
 export class Symbol_Stroke{
    
    /** - crea un Symbol_Stroke
     * @constructor
     * @param {string} Type - tipo de simbolo, solo para comprobaciones
     * @param {string} Color - color del simbolo, si este se deja en undefined o vacio sera 'rgb(0,0,0)'
     * @param {string} width - el ancho de la linea, es en pixeles
     * @param {string} lineDash - si la linea es punteada o no, si esta en undefined o "" sera linea continua, de lo contrario se tomaran como una lista de valores, ej: "2,2" genera una linea punteada con 2 pixeles con color y 2 transparentes
     * @param {string} lineCap - el tipo de terminacion de la linea, valores posibles 'round', 'butt' o'square'
     * @param {string} lineJoin - la forma en la que se quiebra la linea, valores posibles 'round', 'bavel' o'miter'
     * @param {string} ZIndex - el orden del dibujado, se usa cuando se tienen muchos simbolos que se tienen que superponer en cierto ordem, por defecto el zIndex sera el orden de llegada si no se espesifica o es undefined
     */
    constructor(Type, Color, width, lineDash, lineCap, lineJoin, ZIndex){
        this.Type = Type;
        this.Color = Color;
        this.width = width;
        this.lineDash = lineDash;
        this.lineCap = lineCap;
        this.lineJoin = lineJoin;
        this.ZIndex = ZIndex;
    }
}

/** - Parametros de extension custom.
 * @class ExtentConfig
 */
export class ExtentConfig{
    
    /** - crea una extension
     * @constructor
     * @param {string} CRS - Sistema de Referencia usado para interpretar las coordenadas de la extención
     * @param {string} XMin - Coordenadas en X de la esquina SE
     * @param {string} YMin - Coordenadas en Y de la esquina SE
     * @param {string} XMax - Coordenadas en X de la esquina NO
     * @param {string} YMax - Coordenadas en Y de la esquina NO
     */
    constructor(CRS, XMin, YMin, XMax, YMax){
        this.CRS = CRS;
        this.XMin = XMin;
        this.YMin = YMin;
        this.XMax = XMax;
        this.YMax = YMax;
    }
}

/** - Parametros de las herramientas de la UI.
 * @class EnabledTools
 */
export class EnabledTools{
/** - crea una extension
     * @constructor
     * @param {string} LayerDisplay - Text.
     * @param {TopTools} TopTools - Text.
     * @param {string} BigLogo - Text.
     * @param {string} RightBar - Text.
     * @param {string} BottomBar - Text.
     * @param {string} StartQuikHelp - Text.
     * @param {string} NorthArrow - Text.
     * @param {string} ScaleBar - Text.
     */
    constructor(LayerDisplay, TopTools, BigLogo, RightBar, BottomBar, StartQuikHelp, NorthArrow, ScaleBar){
        this.LayerDisplay = LayerDisplay;
        this.TopTools = TopTools;
        this.BigLogo = BigLogo;
        this.RightBar = RightBar;
        this.BottomBar = BottomBar;
        this.StartQuikHelp = StartQuikHelp;
        this.NorthArrow = NorthArrow;
        this.ScaleBar = ScaleBar;
    }
}

/** - Parametros de los componentes de la barra superior.
 * @class TopTools
 */
export class TopTools{
    /** - crea una extension
     * @constructor
     * @param {string} JustifyContent - es el tipo de JustifyContent que el Flex del menu usará.
     * @param {[UIComponent]} UIComponents - Datos de los UIComponents de la barra superior
     */
    constructor(JustifyContent, UIComponents){
        this.JustifyContent = JustifyContent;
        this.UIComponents = UIComponents;
    }
}

/** - Parametros de un componente de la UI.
 * @class UIComponent
 */
export class UIComponent{
    /** - crea una extension
     * @constructor
     * @param {boolean} InUI - booleano que determina si se agrega a la ui este elemento o se ignora
     * @param {string} Type - Tipo del componente (button, separator, container, etc)
     * @param {string} ID - ID del componente
     * @param {string} Paremeters - Coordenadas en X de la esquina SE
     */
    constructor(InUI, Type, ID, Paremeters){
        this.InUI = InUI;
        this.Type = Type;
        this.ID = ID;
        this.Paremeters = Paremeters;
    }
}

/** - Parametros de un componente de la UI.
 * @class ComponentParems
 */
export class ComponentParems{
    /** - crea una extension
     * @constructor
     * @param {boolean} IsEnabled - bool que controla si el componente esta habilitadp (y si es interactuable)
     * @param {boolean} IsVisible - bool que controla si el componente esta visible (y si es interactuable)
     * @param {string} Name - Nombre del componente
     * @param {string} ToolTip - Datos del Tooltip del componente
     * @param {[FunctionConfig]} Functions - Funciones asociados al componente
     */
    constructor(IsEnabled, IsVisible, Name, ToolTip, Functions){
        this.IsEnabled = IsEnabled;
        this.IsVisible = IsVisible;
        this.Name = Name;
        this.ToolTip = ToolTip;
        this.Functions = Functions;
    }
}

/** - Parametros de un componente de la UI.
 * @class FunctionConfig
 */
export class FunctionConfig{
    /** - crea una extension
     * @constructor
     * @param {string} FunctionName - Nombre de la funcion
     * @param {{}} FunctionParams - Parametros de la funcion
     */
    constructor(FunctionName, FunctionParams){
        this.FunctionName = FunctionName;
        this.FunctionParams = FunctionParams;
    }
}